/*
 * KO Cabinetry site interactions. Vanilla JS, no dependencies.
 * Each feature binds to data-* hooks and re-binds when the theme editor
 * reloads a section.
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = window.Shopify && window.Shopify.designMode;
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const track = (name, data = {}) => {
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) window.Shopify.analytics.publish(`ko_${name}`, data);
      if (window.dataLayer) window.dataLayer.push({ event: `ko_${name}`, ...data });
    } catch (e) {
      /* never block the UI on analytics */
    }
  };

  /* ---------------- Enquiry backup ---------------- */
  // Copies each enquiry to the Google Sheet backup (Theme settings > KO Business > Enquiry backup URL),
  // alongside Shopify's own contact email. It never blocks or changes the real submission.
  let lastBackup = '';
  const backupEnquiry = (form) => {
    const cfg = window.koEnquiryBackup;
    if (!cfg || !cfg.url || !form) return;
    try {
      const body = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        const m = key.match(/^contact\[(.+)\]$/);
        if (m && typeof value === 'string' && value.trim()) body.append(m[1], value.trim());
      });
      const fingerprint = body.toString();
      if (fingerprint === lastBackup) return;
      lastBackup = fingerprint;
      body.append('_k', cfg.key || '');
      body.append('_page', location.href);
      if (!(navigator.sendBeacon && navigator.sendBeacon(cfg.url, body))) {
        fetch(cfg.url, { method: 'POST', mode: 'no-cors', body, keepalive: true }).catch(() => {});
      }
    } catch (e) {
      /* the backup must never get in the way of the enquiry itself */
    }
  };
  window.koBackupEnquiry = backupEnquiry;

  // Leave empty optional answers out of the enquiry email, so it only lists what the customer filled in.
  // The formdata event also runs when Shopify's spam check submits the form after its challenge.
  function initEnquiryTidy(root = document) {
    $$('input[name="form_type"][value="contact"]', root).forEach((marker) => {
      const form = marker.form;
      if (!form || form.dataset.koTidy) return;
      form.dataset.koTidy = 'true';
      form.addEventListener('formdata', (e) => {
        Array.from(e.formData.entries()).forEach(([key, value]) => {
          if (key.startsWith('contact[') && typeof value === 'string' && !value.trim()) e.formData.delete(key);
        });
      });
    });
  }

  /* ---------------- Header ---------------- */
  function initHeader() {
    const header = document.querySelector('[data-ko-header]');
    if (!header || header.dataset.bound) return;
    header.dataset.bound = 'true';
    const burger = header.querySelector('[data-ko-burger]');
    // The header always stays in view; it only gains a solid background once the page moves.
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    const setOpen = (open) => {
      header.classList.toggle('is-open', open);
      document.body.classList.toggle('ko-lock', open);
      if (burger) burger.setAttribute('aria-expanded', String(open));
    };
    if (burger) burger.addEventListener('click', () => setOpen(!header.classList.contains('is-open')));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && header.classList.contains('is-open')) setOpen(false);
    });
    header.querySelectorAll('[data-ko-drawer] a').forEach((a) => a.addEventListener('click', () => setOpen(false)));
  }

  /* ---------------- Reveal on scroll ---------------- */
  let revealObserver;
  function initReveal(root = document) {
    const items = $$('[data-ko-reveal]:not(.is-in)', root);
    if (!items.length) return;
    if (reduceMotion || designMode || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    $$('[data-ko-stagger]', root).forEach((group) => {
      $$('[data-ko-reveal]', group).forEach((el, i) => el.style.setProperty('--ko-i', i % 6));
    });
    revealObserver =
      revealObserver ||
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-in');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
      );
    items.forEach((el) => revealObserver.observe(el));
  }

  /* ---------------- Mobile call bar ---------------- */
  function initMobileBar() {
    const bar = document.querySelector('[data-ko-mobilebar]');
    if (!bar || bar.dataset.bound) return;
    bar.dataset.bound = 'true';
    let footerVisible = false;
    const footer = document.querySelector('.ko-footer');
    if (footer && 'IntersectionObserver' in window) {
      new IntersectionObserver((e) => {
        footerVisible = e[0].isIntersecting;
        update();
      }).observe(footer);
    }
    const update = () => {
      const focusedField = document.activeElement && document.activeElement.matches('input, textarea, select');
      bar.classList.toggle('is-visible', window.scrollY > 520 && !footerVisible && !focusedField);
    };
    window.addEventListener('scroll', update, { passive: true });
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', () => setTimeout(update, 50));
    update();
  }

  /* ---------------- Click tracking ---------------- */
  function initTracking() {
    if (document.documentElement.dataset.koTracking) return;
    document.documentElement.dataset.koTracking = 'true';
    document.addEventListener('click', (e) => {
      const t = e.target.closest('[data-ko-track]');
      if (t) track(t.dataset.koTrack, { href: t.getAttribute('href') || '', page: location.pathname });
    });
  }

  /* ---------------- Before / after compare ---------------- */
  function initCompare(root = document) {
    $$('[data-ko-compare]:not([data-bound])', root).forEach((el) => {
      el.dataset.bound = 'true';
      const range = el.querySelector('input[type="range"]');
      const set = (v) => el.style.setProperty('--ko-pos', `${v}%`);
      set(range.value);
      range.addEventListener('input', () => set(range.value));
      let tracked = false;
      range.addEventListener('change', () => {
        if (!tracked) track('compare_used', { page: location.pathname });
        tracked = true;
      });
    });
  }

  /* ---------------- Lightbox gallery ---------------- */
  function initLightbox(root = document) {
    $$('[data-ko-gallery]:not([data-bound])', root).forEach((gallery) => {
      gallery.dataset.bound = 'true';
      const items = $$('[data-ko-lightbox]', gallery);
      if (!items.length) return;
      let dialog = gallery.querySelector('dialog.ko-lightbox');
      if (!dialog) {
        dialog = document.createElement('dialog');
        dialog.className = 'ko-lightbox';
        dialog.innerHTML = `
          <button class="ko-lightbox__close" type="button" aria-label="Close">&times;</button>
          <button class="ko-lightbox__nav ko-lightbox__nav--prev" type="button" aria-label="Previous image">&#8249;</button>
          <figure class="ko-lightbox__figure"><img alt=""><figcaption></figcaption></figure>
          <button class="ko-lightbox__nav ko-lightbox__nav--next" type="button" aria-label="Next image">&#8250;</button>
          <p class="ko-lightbox__count" aria-live="polite"></p>`;
        gallery.appendChild(dialog);
      }
      const img = dialog.querySelector('img');
      const cap = dialog.querySelector('figcaption');
      const count = dialog.querySelector('.ko-lightbox__count');
      let index = 0;
      const show = (i) => {
        index = (i + items.length) % items.length;
        const it = items[index];
        img.src = it.dataset.full || it.getAttribute('href');
        img.alt = it.dataset.alt || '';
        cap.textContent = it.dataset.caption || '';
        count.textContent = `${index + 1} / ${items.length}`;
      };
      items.forEach((it, i) =>
        it.addEventListener('click', (e) => {
          e.preventDefault();
          show(i);
          dialog.showModal();
          document.body.classList.add('ko-lock');
          track('gallery_open', { page: location.pathname });
        }),
      );
      dialog.querySelector('.ko-lightbox__close').addEventListener('click', () => dialog.close());
      dialog.querySelector('.ko-lightbox__nav--prev').addEventListener('click', () => show(index - 1));
      dialog.querySelector('.ko-lightbox__nav--next').addEventListener('click', () => show(index + 1));
      dialog.addEventListener('close', () => document.body.classList.remove('ko-lock'));
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) dialog.close();
      });
      dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') show(index + 1);
        if (e.key === 'ArrowLeft') show(index - 1);
      });
      let startX = 0;
      dialog.addEventListener('touchstart', (e) => (startX = e.touches[0].clientX), { passive: true });
      dialog.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 50) show(index + (dx < 0 ? 1 : -1));
      });
    });
  }

  /* ---------------- Filters (projects hub) ---------------- */
  function initFilters(root = document) {
    $$('[data-ko-filters]:not([data-bound])', root).forEach((wrap) => {
      wrap.dataset.bound = 'true';
      const scope = wrap.closest('[data-ko-filter-scope]') || document;
      const items = $$('[data-ko-tags]', scope);
      const empty = scope.querySelector('[data-ko-filter-empty]');
      const countEl = scope.querySelector('[data-ko-filter-count]');
      const state = {};
      const params = new URLSearchParams(location.search);
      $$('[data-ko-filter-group]', wrap).forEach((g) => {
        const key = g.dataset.koFilterGroup;
        state[key] = params.get(key) || 'all';
      });
      const apply = (push) => {
        let shown = 0;
        items.forEach((it) => {
          const tags = (it.dataset.koTags || '').split(',').map((t) => t.trim());
          const ok = Object.entries(state).every(([k, v]) => v === 'all' || tags.includes(`${k}:${v}`));
          it.hidden = !ok;
          if (ok) shown++;
        });
        $$('[data-ko-filter-group]', wrap).forEach((g) => {
          $$('[data-value]', g).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.value === state[g.dataset.koFilterGroup])));
        });
        if (empty) empty.hidden = shown > 0;
        if (countEl) countEl.textContent = shown;
        if (push) {
          const p = new URLSearchParams();
          Object.entries(state).forEach(([k, v]) => v !== 'all' && p.set(k, v));
          history.replaceState(null, '', `${location.pathname}${p.toString() ? `?${p}` : ''}`);
        }
      };
      wrap.addEventListener('click', (e) => {
        const b = e.target.closest('[data-value]');
        if (!b) return;
        const g = b.closest('[data-ko-filter-group]');
        state[g.dataset.koFilterGroup] = b.dataset.value;
        apply(true);
        track('project_filter', { [g.dataset.koFilterGroup]: b.dataset.value });
      });
      apply(false);
    });
  }

  /* ---------------- Sticky story (scroll-driven steps) ---------------- */
  function initStory(root = document) {
    $$('[data-ko-story]:not([data-bound])', root).forEach((story) => {
      story.dataset.bound = 'true';
      const steps = $$('[data-ko-story-step]', story);
      const media = $$('[data-ko-story-media]', story);
      const bar = story.querySelector('[data-ko-story-progress]');
      const activate = (i) => {
        steps.forEach((s, idx) => s.classList.toggle('is-active', idx === i));
        media.forEach((m, idx) => m.classList.toggle('is-active', idx === i));
        if (bar) bar.style.setProperty('--ko-progress', `${((i + 1) / steps.length) * 100}%`);
      };
      if (!('IntersectionObserver' in window)) return activate(0);
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) activate(steps.indexOf(e.target));
          });
        },
        { rootMargin: '-45% 0px -45% 0px' },
      );
      steps.forEach((s) => io.observe(s));
      activate(0);
    });
  }

  /* ---------------- Count-up numbers ---------------- */
  function initCounters(root = document) {
    const els = $$('[data-ko-count]:not([data-bound])', root);
    if (!els.length) return;
    const run = (el) => {
      const target = parseFloat(el.dataset.koCount);
      const decimals = (el.dataset.koCount.split('.')[1] || '').length;
      if (reduceMotion || designMode) {
        el.textContent = target.toFixed(decimals);
        return;
      }
      const start = performance.now();
      const dur = 1400;
      const step = (now) => {
        const t = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - t, 4);
        el.textContent = (target * eased).toFixed(decimals);
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          run(e.target);
          io.unobserve(e.target);
        }
      });
    });
    els.forEach((el) => {
      el.dataset.bound = 'true';
      io.observe(el);
    });
  }

  /* ---------------- Multi-step quote form ---------------- */
  function initQuote(root = document) {
    $$('[data-ko-quote]:not([data-bound])', root).forEach((wrap) => {
      wrap.dataset.bound = 'true';
      const form = wrap.querySelector('form');
      if (!form) return;
      const steps = $$('[data-ko-quote-step]', wrap);
      const progress = wrap.querySelector('[data-ko-quote-progress]');
      const label = wrap.querySelector('[data-ko-quote-label]');
      const error = wrap.querySelector('[data-ko-quote-error]');
      let current = 0;

      // Pre-select options passed in the URL, e.g. ?type=builder&project=kitchen
      const params = new URLSearchParams(location.search);
      ['type', 'project'].forEach((key) => {
        const val = params.get(key);
        if (!val) return;
        const input = form.querySelector(`[data-ko-param="${key}"][value="${CSS.escape(val)}"], [data-ko-param="${key}"][data-slug="${CSS.escape(val)}"]`);
        if (input) input.checked = true;
      });
      const design = location.hash.startsWith('#design=') ? `${location.origin}/pages/design-studio${location.hash}` : '';
      const designField = form.querySelector('[name="contact[Design link]"]');
      if (designField && design) designField.value = design;
      const pageField = form.querySelector('[name="contact[Page]"]');
      if (pageField) pageField.value = document.referrer || location.href;

      const show = (i) => {
        current = Math.max(0, Math.min(steps.length - 1, i));
        steps.forEach((s, idx) => {
          s.hidden = idx !== current;
          s.setAttribute('aria-hidden', String(idx !== current));
        });
        if (progress) progress.style.setProperty('--ko-progress', `${((current + 1) / steps.length) * 100}%`);
        if (label) label.textContent = `Step ${current + 1} of ${steps.length}`;
        if (error) error.hidden = true;
        $$('[data-ko-quote-prev]', wrap).forEach((b) => (b.hidden = current === 0));
        $$('[data-ko-quote-next]', wrap).forEach((b) => (b.hidden = current === steps.length - 1));
        $$('[data-ko-quote-submit]', wrap).forEach((b) => (b.hidden = current !== steps.length - 1));
      };

      const validStep = () => {
        const step = steps[current];
        const fields = $$('input, select, textarea', step).filter((f) => !f.disabled);
        for (const f of fields) {
          if (f.type === 'radio' && f.required) {
            if (!step.querySelector(`input[name="${CSS.escape(f.name)}"]:checked`)) {
              f.focus();
              return false;
            }
          } else if (!f.checkValidity()) {
            f.reportValidity();
            return false;
          }
        }
        return true;
      };

      wrap.addEventListener('click', (e) => {
        if (e.target.closest('[data-ko-quote-next]')) {
          if (!validStep()) {
            if (error) error.hidden = false;
            return;
          }
          track('quote_step', { step: current + 1 });
          show(current + 1);
          wrap.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        }
        if (e.target.closest('[data-ko-quote-prev]')) show(current - 1);
      });

      // Auto-advance single-choice steps for speed on mobile.
      steps.forEach((step, idx) => {
        if (!step.hasAttribute('data-ko-autoadvance')) return;
        step.addEventListener('change', (e) => {
          if (e.target.type === 'radio') setTimeout(() => idx === current && show(current + 1), 260);
        });
      });

      const joinFields = () => {
        $$('[data-ko-join]', form).forEach((h) => {
          h.value = $$(`input[name="${h.dataset.koJoin}"]:checked`, form)
            .map((i) => i.value)
            .join(', ');
        });
      };
      form.addEventListener('change', joinFields);

      // Submit natively: Shopify's spam protection (hCaptcha) attaches its token to a
      // normal form submission, and the page reloads with the thank-you message.
      form.addEventListener('submit', (e) => {
        joinFields();
        if (!validStep()) {
          e.preventDefault();
          return;
        }
        const btn = form.querySelector('[data-ko-quote-submit]');
        if (btn) btn.classList.add('is-loading');
        backupEnquiry(form);
        track('quote_submit', { page: location.pathname });
      });

      show(0);
    });
  }

  /* ---------------- Simple contact forms (single step) ---------------- */
  function initSimpleForms(root = document) {
    $$('form[data-ko-form-track]:not([data-bound])', root).forEach((form) => {
      form.dataset.bound = 'true';
      form.addEventListener('submit', () => track(form.dataset.koFormTrack, { page: location.pathname }));
    });
  }

  /* ---------------- Wide tables in articles ---------------- */
  // Tables that scroll sideways on phones can be reached and scrolled with a keyboard too.
  function initTables(root = document) {
    $$('.ko-article__body table:not([tabindex])', root).forEach((table) => {
      table.tabIndex = 0;
      table.setAttribute('aria-label', table.querySelector('caption')?.textContent.trim() || 'Comparison table, scroll sideways to see all columns');
    });
  }

  function initAll(root = document) {
    initReveal(root);
    initCompare(root);
    initLightbox(root);
    initFilters(root);
    initStory(root);
    initCounters(root);
    initQuote(root);
    initSimpleForms(root);
    initEnquiryTidy(root);
    initTables(root);
  }

  window.koReady = true;
  document.documentElement.classList.add('ko-js');
  const boot = () => {
    initHeader();
    initMobileBar();
    initTracking();
    initAll();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => {
    initHeader();
    initMobileBar();
    initAll(e.target);
  });

  // Theme editor: show the block being edited (open its FAQ answer, scroll a carousel to it).
  document.addEventListener('shopify:block:select', (e) => {
    const el = e.target;
    if (el.matches('details')) el.open = true;
    const track = el.parentElement;
    if (track && track.scrollWidth > track.clientWidth + 4) {
      track.scrollTo({ left: el.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    }
  });
})();
